import { Box, Button, Typography } from "@mui/material";
import {
  isRouteErrorResponse,
  useLocation,
  useNavigate,
  useNavigation,
  useRouteError,
} from "react-router-dom";

function messageFor(error: unknown): string | undefined {
  if (isRouteErrorResponse(error)) return `${error.status} ${error.statusText}`;
  if (error instanceof Error) return error.message;
  return undefined;
}

/**
 * The `errorElement` every route gets — see `withErrorElement` in src/router.tsx.
 *
 * Attaching it per route rather than at the root is the whole point: React Router renders an
 * error in place of the route that failed, so this lands inside the shell's content region and
 * the nav, header and footer stay where they are. Without it an error bubbles to the root route
 * and React Router replaces the shell itself with its own default error page.
 *
 * "Try again" re-navigates to the current URL rather than calling `revalidate()`, which does not
 * clear an error boundary — a fresh navigation does, and it re-runs the loader on the way. Either
 * way the user is never stuck: unlike a class error boundary, which stays tripped until the page
 * is reloaded, simply navigating elsewhere also recovers.
 */
const RouteError = () => {
  const error = useRouteError();
  const navigate = useNavigate();
  const location = useLocation();
  const navigation = useNavigation();
  const detail = messageFor(error);

  if (import.meta.env.DEV) console.error("Route error:", error);

  return (
    <Box sx={{ p: 4, textAlign: "center" }}>
      <Typography variant="h6" gutterBottom>
        Something went wrong
      </Typography>
      <Typography variant="body2" gutterBottom sx={{ color: "text.secondary" }}>
        This page couldn&apos;t be loaded. If the problem persists, contact your
        administrator.
      </Typography>
      {detail && import.meta.env.DEV && (
        <Typography
          variant="caption"
          component="p"
          sx={{ color: "text.secondary", mb: 2, fontFamily: "monospace" }}
        >
          {detail}
        </Typography>
      )}
      <Box sx={{ display: "flex", gap: 2, justifyContent: "center", mt: 2 }}>
        <Button
          variant="contained"
          disabled={navigation.state !== "idle"}
          onClick={() =>
            navigate(location.pathname + location.search, { replace: true })
          }
        >
          Try again
        </Button>
        <Button variant="outlined" onClick={() => navigate("/dashboard")}>
          Go to dashboard
        </Button>
      </Box>
    </Box>
  );
};

export default RouteError;
